// src/components/ShippingRecord/AddDisputePopin/DisputeFormContent.tsx
import { useFormContext } from "react-hook-form";
import dateFormat from "dateformat";
import { Text, Button, Popin, Modal } from "$components";
import { useDisputeForm } from "../context/DisputeFormContext";
import { useStyles } from "../styles";
import useJStyles from "$hooks/useJStyles";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { DisputeFormInfo } from "./DisputeFormInfo";
import { DisputeFormAttachments } from "./DisputeFormAttachments";
import { EditMode } from "../hooks/useDisputeFormLogic";
import { ShippingFileType, ShippingRecordDisputeType } from "$types";
import { FormValues } from "..";

type EditModeConfigType = {
  title: string;
  isSubmitButtonDisplayed: boolean;
  submitButtonLabel: string;
  cancelButtonLabel: string;
};

const EditModeConfig: { [key in EditMode]: EditModeConfigType } = {
  [EditMode.CREATE]: {
    title: "Ajouter un litige",
    isSubmitButtonDisplayed: true,
    submitButtonLabel: "Ajouter le litige",
    cancelButtonLabel: "Annuler",
  },
  [EditMode.EDIT]: {
    title: "Editer un litige",
    isSubmitButtonDisplayed: true,
    submitButtonLabel: "Enregistrer",
    cancelButtonLabel: "Annuler",
  },
  [EditMode.READ_ONLY]: {
    title: "Informations du litige",
    isSubmitButtonDisplayed: false,
    submitButtonLabel: "",
    cancelButtonLabel: "Retour",
  },
};

type Props = {
  onSubmit: () => void;
  onClose: () => void;
};

export default function DisputeFormContent({ onSubmit, onClose }: Props) {
  const form = useFormContext();
  const { jclasses } = useJStyles();
  const classes = useStyles();

  const {
    editMode,
    isOpen,
    dispute,
    type,
    linkedEntityId,
    isFetching,
    isDeleteModalOpen,
    toggleDeleteModal,
    handleDisputeClose,
    shippingRecord,
    selectedDispute,
    createShippingRecordDispute,
    updateShippingRecordDispute,
  } = useDisputeForm();

  const handleFormSubmit = async (formValues: FormValues) => {
    const fileTransform = formValues.media.map((media) => ({
      mainApiMediaId: media._id,
      name: media.name,
      type: ShippingFileType.OTHER,
      url: media.url,
    }));

    if (editMode === EditMode.EDIT) {
      await updateShippingRecordDispute(selectedDispute?.id, {
        type: formValues.type,
        text: formValues.text,
        linkedEntityId: formValues.linkedEntityId,
        files: fileTransform,
      });
    }

    if (editMode === EditMode.CREATE) {
      await createShippingRecordDispute({
        type: formValues.type,
        text: formValues.text,
        shippingRecordId: shippingRecord?.id,
        files: fileTransform,
        linkedEntityId:
          formValues.type === ShippingRecordDisputeType.SHIPPING_RECORD
            ? shippingRecord?.id
            : formValues.linkedEntityId,
      });
    }

    onSubmit();
    onClose();
  };

  return (
    <Popin
      openPopin={isOpen}
      toggleDrawer={onClose}
      containerClassName={classes.popinContainer}
    >
      <div className={classes.wrapper}>
        <Text type="h2">{EditModeConfig[editMode].title}</Text>
        {editMode !== EditMode.CREATE && (
          <div className={classes.lineContainer}>
            <div className={classes.inputContainer}>
              <Text type="disabled">
                {`Ouvert le : ${dateFormat(
                  dispute?.createdAt,
                  DEFAULT_DATE_FORMAT
                )} `}
              </Text>
              <Text type="disabled">
                {`\xa0| Créé par : ${dispute?.createdBy}`}
              </Text>
            </div>
            {editMode === EditMode.READ_ONLY ? (
              <Text type="disabled">
                {`Clôturé le : ${dateFormat(
                  dispute?.closedAt,
                  DEFAULT_DATE_FORMAT
                )} `}
              </Text>
            ) : (
              <Button onClick={() => toggleDeleteModal(true)}>
                Clôturer le litige
              </Button>
            )}
          </div>
        )}

        <form onSubmit={form.handleSubmit(handleFormSubmit)}>
          <DisputeFormInfo />
          <DisputeFormAttachments />
          <div className={classes.buttonsContainer}>
            <Button color="secondary" onClick={onClose}>
              {EditModeConfig[editMode].cancelButtonLabel}
            </Button>
            {EditModeConfig[editMode].isSubmitButtonDisplayed && (
              <Button
                type="submit"
                disabled={
                  isFetching ||
                  !type ||
                  (type !== ShippingRecordDisputeType.SHIPPING_RECORD &&
                    !linkedEntityId)
                }
              >
                {EditModeConfig[editMode].submitButtonLabel}
              </Button>
            )}
          </div>
        </form>
      </div>

      <Modal
        title="Clôture du litige dans le dossier"
        textValidate="Oui"
        textCancel="Non"
        open={isDeleteModalOpen}
        onValidate={handleDisputeClose}
        onCancel={() => toggleDeleteModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de clôturer le litige.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </Popin>
  );
}
